import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Building2, Users, Car, Plus, Search, UserPlus, X, RefreshCw, Shield, ChevronDown, ChevronUp, Power, PowerOff, Image as ImageIcon,
} from 'lucide-react';
import { superAdminService, EmpresaData, SuperAdminStats } from '../../services/superadmin';
import { useConfirm } from '../../components/ConfirmDialog';
import { formatCNPJ } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { CnpjInput, PhoneInput, CepInput } from '../../components/MaskedInputs';
import CampoSenha from '../../components/CampoSenha';
import RequisitosDeSenha from '../../components/RequisitosDeSenha';
import CampoCidadeUf from '../../components/CampoCidadeUf';
import { buscarCep } from '../../services/localidades';
import { cnpjValido } from '../../utils/documentos';
import UsuariosGlobais from './UsuariosGlobais';
import AuditoriaList from '../auditoria/AuditoriaList';
import { AcoesDaLinha, BotaoAcao, BotaoExcluir } from '../../components/BotoesAcao';
import { mensagemDoErro } from '../../utils/erros';

type Aba = 'empresas' | 'usuarios' | 'auditoria';

const SuperAdminDashboard: React.FC = () => {
  const confirm = useConfirm();
  // Aba na URL: permite mandar link direto para a lista de usuarios e mantem a
  // secao apos um refresh, em vez de sempre voltar para Empresas.
  const [searchParams, setSearchParams] = useSearchParams();
  const abaUrl = searchParams.get('aba');
  const aba: Aba = abaUrl === 'usuarios' || abaUrl === 'auditoria' ? abaUrl : 'empresas';
  const setAba = (nova: Aba) => {
    setSearchParams(nova === 'empresas' ? {} : { aba: nova }, { replace: true });
  };
  const [stats, setStats] = useState<SuperAdminStats | null>(null);
  const [empresas, setEmpresas] = useState<EmpresaData[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroPlano, setFiltroPlano] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState<number | null>(null);
  const [expandedEmpresa, setExpandedEmpresa] = useState<number | null>(null);
  const [empresaUsers, setEmpresaUsers] = useState<any[]>([]);
  const [newEmpresa, setNewEmpresa] = useState({
    nome: '', cnpj: '', razao_social: '', telefone: '', email: '',
    endereco: '', cidade: '', estado: '', cep: '', plano: 'basico',
    admin_name: '', admin_email: '', admin_password: '', admin_password_confirmation: '',
  });
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', password_confirmation: '', role: 'operador' });
  const [cepBuscando, setCepBuscando] = useState(false);
  const [cepAviso, setCepAviso] = useState('');

  /**
   * Preenche UF, cidade e endereco a partir do CEP digitado.
   *
   * So dispara com os 8 digitos completos: consultar a cada tecla geraria oito
   * requisicoes para descobrir um endereco so.
   */
  const preencherPeloCep = async (digitos: string) => {
    setNewEmpresa(prev => ({ ...prev, cep: digitos }));

    if (digitos.length !== 8) {
      setCepAviso('');
      return;
    }

    setCepBuscando(true);
    setCepAviso('');
    try {
      const endereco = await buscarCep(digitos);
      if (!endereco) {
        setCepAviso('CEP nao encontrado. Preencha os campos manualmente.');
        return;
      }
      setNewEmpresa(prev => ({
        ...prev,
        cep: digitos,
        estado: endereco.uf,
        cidade: endereco.cidade,
        // Endereco ja digitado nao e sobrescrito: o numero e o complemento que a
        // pessoa acabou de escrever nao vem no CEP e se perderiam.
        endereco: prev.endereco.trim() === ''
          ? [endereco.logradouro, endereco.bairro].filter(Boolean).join(' - ')
          : prev.endereco,
      }));
    } catch {
      setCepAviso('Nao foi possivel consultar o CEP agora. Preencha os campos manualmente.');
    } finally {
      setCepBuscando(false);
    }
  };
  // Guarda o arquivo escolhido e a previa. A logo so sobe depois que a empresa
  // existe, porque o envio precisa do id dela.
  const [logoNova, setLogoNova] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, empresasRes] = await Promise.all([
        superAdminService.getStats(),
        superAdminService.getEmpresas({ search, plano: filtroPlano }),
      ]);
      if (statsRes.success) setStats(statsRes.data);
      if (empresasRes.success) setEmpresas(empresasRes.data);
    } catch (err) {
      toast.error(mensagemDoErro(err, 'Erro ao carregar dados'));
    } finally {
      setLoading(false);
    }
  }, [search, filtroPlano]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const trocarLogo = async (empresaId: number, arquivo: File) => {
    try {
      const res = await superAdminService.enviarLogo(empresaId, arquivo);
      toast.success(res.message);
      fetchData();
    } catch (err: any) {
      toast.error(mensagemDoErro(err, 'Não foi possível enviar a logomarca.'));
    }
  };

  const handleCreateEmpresa = async (e: React.FormEvent) => {
    e.preventDefault();

    // Confere aqui para o erro aparecer antes da ida ao servidor: uma senha
    // digitada errada duas vezes deixaria o administrador da nova empresa sem
    // conseguir entrar, e sem e-mail configurado nao ha como se recuperar.
    if (newEmpresa.admin_password !== newEmpresa.admin_password_confirmation) {
      toast.error('A confirmação não confere com a senha digitada.');
      return;
    }

    try {
      const res = await superAdminService.createEmpresa(newEmpresa);
      if (res.success) {
        if (logoNova && res.data?.id) {
          try {
            await superAdminService.enviarLogo(res.data.id, logoNova);
          } catch (err: any) {
            // A empresa foi criada; falhar a logo nao pode desfazer isso nem
            // parecer que o cadastro inteiro falhou.
            toast.error(mensagemDoErro(err, 'Empresa criada, mas a logo não subiu. Envie de novo pela lista.'));
          }
        }
        toast.success('Empresa criada com sucesso!');
        setShowCreateModal(false);
        setNewEmpresa({ nome: '', cnpj: '', razao_social: '', telefone: '', email: '', endereco: '', cidade: '', estado: '', cep: '', plano: 'basico', admin_name: '', admin_email: '', admin_password: '', admin_password_confirmation: '' });
        setCepAviso('');
        setLogoNova(null);
        setLogoPreview(null);
        fetchData();
      }
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors) { toast.error(Object.values(errors).flat()[0] as string); }
      else { toast.error(mensagemDoErro(err, 'Erro ao criar empresa')); }
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      const res = await superAdminService.toggleStatus(id);
      if (res.success) { toast.success(res.message); fetchData(); }
    } catch (err: any) { toast.error(mensagemDoErro(err, 'Erro ao alterar status')); }
  };

  const handleDelete = async (id: number, nome: string) => {
    if (!(await confirm({
      title: 'Excluir empresa',
      message: `Tem certeza que deseja excluir a empresa "${nome}"? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      variant: 'danger',
    }))) return;
    try {
      const res = await superAdminService.deleteEmpresa(id);
      if (res.success) { toast.success('Empresa excluída'); fetchData(); }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao excluir');
    }
  };

  const handleExpandEmpresa = async (id: number) => {
    if (expandedEmpresa === id) { setExpandedEmpresa(null); return; }
    try {
      const res = await superAdminService.getUsers(id);
      if (res.success) { setEmpresaUsers(res.data); setExpandedEmpresa(id); }
    } catch (err: any) { toast.error(mensagemDoErro(err, 'Erro ao carregar os usuários')); }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newUser.password !== newUser.password_confirmation) {
      toast.error('A confirmação não confere com a senha digitada.');
      return;
    }
    if (!showUserModal) return;
    try {
      const res = await superAdminService.addUser(showUserModal, newUser);
      if (res.success) {
        toast.success('Usuário criado');
        setShowUserModal(null);
        setNewUser({ name: '', email: '', password: '', password_confirmation: '', role: 'operador' });
        handleExpandEmpresa(showUserModal);
        fetchData();
      }
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors) { toast.error(Object.values(errors).flat()[0] as string); }
      else { toast.error(mensagemDoErro(err, 'Erro ao criar o usuário')); }
    }
  };

  const handleRemoveUser = async (empresaId: number, userId: number, nome: string) => {
    if (!(await confirm({
      title: 'Remover usuário',
      message: `Remover o usuário "${nome}"?`,
      confirmLabel: 'Remover',
      variant: 'danger',
    }))) return;
    try {
      const res = await superAdminService.removeUser(empresaId, userId);
      if (res.success) { toast.success('Usuário removido'); handleExpandEmpresa(empresaId); fetchData(); }
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Erro ao remover'); }
  };

  const planoColor = (plano: string) => {
    switch (plano) {
      case 'enterprise': return 'bg-purple-100 text-purple-700';
      case 'profissional': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-red-600" />
            <h1 className="text-2xl font-bold text-gray-900">Super Admin</h1>
          </div>
          <p className="text-gray-500 mt-1">Administracao de todas as empresas do sistema</p>
        </div>
        {aba === 'empresas' && (
          <button onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="h-4 w-4" /> Nova empresa
          </button>
        )}
      </div>

      {/* Abas */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" aria-label="Seções da administração">
          {([['empresas', 'Empresas'], ['usuarios', 'Usuários'], ['auditoria', 'Auditoria']] as const).map(([chave, rotulo]) => (
            <button
              key={chave}
              onClick={() => setAba(chave)}
              aria-current={aba === chave ? 'page' : undefined}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                aba === chave
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {rotulo}
            </button>
          ))}
        </nav>
      </div>

      {aba === 'usuarios' && (
        <UsuariosGlobais empresas={empresas.map((e) => ({ id: e.id, nome: e.nome }))} />
      )}

      {aba === 'auditoria' && (
        <AuditoriaList empresas={empresas.map((e) => ({ id: e.id, nome: e.nome }))} />
      )}

      {aba === 'empresas' && (
        <>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_empresas}</p>
                  <p className="text-xs text-gray-500">Empresas ({stats.empresas_ativas} ativas)</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_usuarios}</p>
                  <p className="text-xs text-gray-500">Usuários</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border p-5">
              <p className="text-sm text-gray-500 mb-2">Por Plano</p>
              <div className="flex gap-2">
                <span className="px-2 py-1 text-xs rounded-full bg-gray-100">{stats.por_plano.basico} Basico</span>
                <span className="px-2 py-1 text-xs rounded-full bg-blue-100">{stats.por_plano.profissional} Pro</span>
                <span className="px-2 py-1 text-xs rounded-full bg-purple-100">{stats.por_plano.enterprise} Ent</span>
              </div>
            </div>
            <div className="bg-white rounded-xl border p-5 flex items-center justify-center">
              <button onClick={fetchData} className="flex items-center gap-2 text-gray-500 hover:text-blue-600">
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} /> Atualizar
              </button>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nome, CNPJ..."
              className="pl-10 w-full py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <select value={filtroPlano} onChange={e => setFiltroPlano(e.target.value)}
            className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500">
            <option value="">Todos os planos</option>
            <option value="basico">Basico</option>
            <option value="profissional">Profissional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>

        {/* Lista de Empresas */}
        <div className="space-y-3">
          {empresas.map((empresa) => (
            <div key={empresa.id} className="bg-white rounded-xl border">
              <div className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  {/* A logo tambem serve de botao de troca: e onde a pessoa olha
                      quando quer mudar a imagem, e nao num menu escondido. */}
                  <label
                    title={empresa.logo ? 'Trocar a logomarca' : 'Enviar uma logomarca'}
                    className="w-14 h-14 shrink-0 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500"
                  >
                    {empresa.logo
                      ? <img src={empresa.logo} alt={`Logomarca da ${empresa.nome}`} className="w-full h-full object-contain" />
                      : <ImageIcon className="h-5 w-5 text-gray-300" />}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="sr-only"
                      aria-label={`Logomarca da ${empresa.nome}`}
                      onChange={e => {
                        const arquivo = e.target.files?.[0];
                        if (arquivo) trocarLogo(empresa.id, arquivo);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-900">{empresa.nome}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${planoColor(empresa.plano)}`}>
                        {empresa.plano}
                      </span>
                      {!empresa.ativo && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">Inativa</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      CNPJ: {formatCNPJ(empresa.cnpj)} | {empresa.cidade || '-'}/{empresa.estado || '-'}
                    </p>
                    <div className="flex gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {empresa.users_count || 0} usuarios</span>
                      <span className="flex items-center gap-1"><Car className="h-3.5 w-3.5" /> {empresa.veiculos_count || 0} veiculos</span>
                      <span>{empresa.motoristas_count || 0} motoristas</span>
                    </div>
                  </div>
                  <AcoesDaLinha>
                    <BotaoAcao
                      tom="cinza"
                      icone={expandedEmpresa === empresa.id ? ChevronUp : ChevronDown}
                      onClick={() => handleExpandEmpresa(empresa.id)}
                      title="Ver usuários da empresa"
                    >
                      Usuários
                    </BotaoAcao>
                    <BotaoAcao tom="azul" icone={UserPlus} onClick={() => setShowUserModal(empresa.id)}>
                      Novo usuário
                    </BotaoAcao>
                    {empresa.ativo ? (
                      <BotaoAcao tom="ambar" icone={PowerOff} onClick={() => handleToggleStatus(empresa.id)}>
                        Desativar
                      </BotaoAcao>
                    ) : (
                      <BotaoAcao tom="verde" icone={Power} onClick={() => handleToggleStatus(empresa.id)}>
                        Ativar
                      </BotaoAcao>
                    )}
                    <BotaoExcluir onClick={() => handleDelete(empresa.id, empresa.nome)} />
                  </AcoesDaLinha>
                </div>
              </div>

              {/* Usuarios expandidos */}
              {expandedEmpresa === empresa.id && (
                <div className="border-t bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Usuários da empresa</h4>
                  {empresaUsers.length > 0 ? (
                    <div className="space-y-2">
                      {empresaUsers.map((user: any) => (
                        <div key={user.id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email} · {user.role}</p>
                          </div>
                          <BotaoExcluir onClick={() => handleRemoveUser(empresa.id, user.id, user.name)} title="Remover usuário" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Nenhum usuário encontrado</p>
                  )}
                </div>
              )}
            </div>
          ))}

          {empresas.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-400">
              <Building2 className="h-12 w-12 mx-auto mb-3" />
              <p>Nenhuma empresa encontrada</p>
            </div>
          )}
        </div>
        </>
      )}

      {/* Modal Criar Empresa */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">Nova empresa</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleCreateEmpresa} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input required value={newEmpresa.nome} onChange={e => setNewEmpresa({...newEmpresa, nome: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <CnpjInput name="cnpj" label="CNPJ" required value={newEmpresa.cnpj}
                    onChange={e => setNewEmpresa({...newEmpresa, cnpj: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                  {newEmpresa.cnpj.length === 14 && !cnpjValido(newEmpresa.cnpj) && (
                    <p className="mt-1 text-xs text-red-600">CNPJ inválido. Confira os números.</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="logo-empresa" className="block text-sm font-medium text-gray-700 mb-1">
                    Logomarca
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-lg border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                      {logoPreview
                        ? <img src={logoPreview} alt="Prévia da logomarca" className="w-full h-full object-contain" />
                        : <ImageIcon className="h-7 w-7 text-gray-300" />}
                    </div>
                    <div className="min-w-0">
                      <input
                        id="logo-empresa"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={e => {
                          const arquivo = e.target.files?.[0] || null;
                          setLogoNova(arquivo);
                          // A prévia sai do próprio arquivo escolhido: mostrar antes
                          // de enviar evita descobrir a logo errada só depois.
                          setLogoPreview(arquivo ? URL.createObjectURL(arquivo) : null);
                        }}
                        className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:text-sm file:font-medium file:bg-white file:text-gray-700 hover:file:bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        JPG, PNG ou WEBP, até 2 MB. Aparece no topo do sistema para os usuários
                        desta empresa.
                      </p>
                      {logoNova && (
                        <button type="button"
                          onClick={() => { setLogoNova(null); setLogoPreview(null); }}
                          className="text-xs text-red-600 hover:underline mt-1">
                          Remover imagem escolhida
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Razao Social</label>
                  <input value={newEmpresa.razao_social} onChange={e => setNewEmpresa({...newEmpresa, razao_social: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plano *</label>
                  <select required value={newEmpresa.plano} onChange={e => setNewEmpresa({...newEmpresa, plano: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="basico">Basico</option>
                    <option value="profissional">Profissional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                  <input type="email" value={newEmpresa.email} onChange={e => setNewEmpresa({...newEmpresa, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <PhoneInput name="telefone" label="Telefone" value={newEmpresa.telefone}
                    onChange={e => setNewEmpresa({...newEmpresa, telefone: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                {/* CEP primeiro: é ele que preenche UF, cidade e logradouro. */}
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <CepInput name="cep" label="CEP" value={newEmpresa.cep}
                      onChange={e => preencherPeloCep(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                    {cepBuscando && (
                      <p className="mt-1 text-xs text-gray-500">Buscando endereço...</p>
                    )}
                    {cepAviso && (
                      <p className="mt-1 text-xs text-amber-600">{cepAviso}</p>
                    )}
                  </div>
                  <CampoCidadeUf
                    uf={newEmpresa.estado}
                    cidade={newEmpresa.cidade}
                    onChange={({ uf, cidade }) => setNewEmpresa(prev => ({ ...prev, estado: uf, cidade }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                  <input value={newEmpresa.endereco} onChange={e => setNewEmpresa({...newEmpresa, endereco: e.target.value})}
                    placeholder="Rua, número e complemento"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <hr />
              <h3 className="text-sm font-semibold text-gray-700">Administrador da empresa</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input required value={newEmpresa.admin_name} onChange={e => setNewEmpresa({...newEmpresa, admin_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input required type="email" value={newEmpresa.admin_email} onChange={e => setNewEmpresa({...newEmpresa, admin_email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label htmlFor="senha-admin-empresa" className="block text-sm font-medium text-gray-700 mb-1">Senha *</label>
                  <CampoSenha
                    id="senha-admin-empresa"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="Crie uma senha forte"
                    value={newEmpresa.admin_password}
                    onChange={e => setNewEmpresa({...newEmpresa, admin_password: e.target.value})}
                  />
                  <p className="mt-2 text-xs font-medium text-gray-700">A senha precisa ter, obrigatoriamente:</p>
                  <RequisitosDeSenha senha={newEmpresa.admin_password} />
                </div>
                <div>
                  <label htmlFor="confirmar-senha-admin-empresa" className="block text-sm font-medium text-gray-700 mb-1">Confirmar senha *</label>
                  <CampoSenha
                    id="confirmar-senha-admin-empresa"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="Repita a senha"
                    value={newEmpresa.admin_password_confirmation}
                    onChange={e => setNewEmpresa({...newEmpresa, admin_password_confirmation: e.target.value})}
                  />
                  {newEmpresa.admin_password_confirmation
                    && newEmpresa.admin_password !== newEmpresa.admin_password_confirmation && (
                    <p role="alert" className="text-xs text-red-600 mt-1">
                      A confirmação não confere com a senha digitada.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Criar Empresa</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Adicionar Usuario */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">Adicionar usuário</h2>
              <button onClick={() => setShowUserModal(null)} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleAddUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input required value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input required type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label htmlFor="senha-novo-usuario" className="block text-sm font-medium text-gray-700 mb-1">Senha *</label>
                <CampoSenha
                  id="senha-novo-usuario"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Crie uma senha forte"
                  value={newUser.password}
                  onChange={e => setNewUser({...newUser, password: e.target.value})}
                />
                <p className="mt-2 text-xs font-medium text-gray-700">A senha precisa ter, obrigatoriamente:</p>
                <RequisitosDeSenha senha={newUser.password} />
              </div>
              <div>
                <label htmlFor="confirmar-senha-novo-usuario" className="block text-sm font-medium text-gray-700 mb-1">Confirmar senha *</label>
                <CampoSenha
                  id="confirmar-senha-novo-usuario"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Repita a senha"
                  value={newUser.password_confirmation}
                  onChange={e => setNewUser({...newUser, password_confirmation: e.target.value})}
                />
                {newUser.password_confirmation && newUser.password !== newUser.password_confirmation && (
                  <p role="alert" className="text-xs text-red-600 mt-1">
                    A confirmação não confere com a senha digitada.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Perfil *</label>
                <select required value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="admin">Administrador</option>
                  <option value="gestor">Gestor</option>
                  <option value="operador">Operador</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowUserModal(null)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Criar usuário</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
